import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import { useLang } from "@/context/LangContext";

const LOCATION_IMG = "/site_assets/training_photo3.jpg";

export default function TrainingsPage() {
    const { t } = useLang();
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "", age: "",
        training_type: "individual", preferred_date: "",
        experience_level: "beginner", goals: "",
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/signups", { ...form, age: Number(form.age) });
            setSubmitted(true);
            toast.success("Application received", { description: "Our team will contact you within 24 hours." });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally { setLoading(false); }
    };

    return (
        <div data-testid="trainings-page" className="min-h-[calc(100vh-5rem)]">
            <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative bg-black min-h-[480px] lg:min-h-[calc(100vh-5rem)]">
                    <img src={LOCATION_IMG} alt="Training" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                    <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/95" />
                    <div className="relative z-10 p-10 lg:p-16 h-full flex flex-col justify-between">
                        <div>
                            <div className="mb-5">
                                <span className="font-body text-xs uppercase tracking-[0.25em] text-volt">
                                    {t("trainings_title")}
                                </span>
                            </div>
                            <h1 className="text-5xl md:text-6xl font-heading uppercase leading-[0.95]">
                                {t("trainings_hero")}
                            </h1>
                            <p className="mt-6 text-white/70 max-w-md">{t("trainings_body")}</p>
                        </div>
                        <div className="mt-10 space-y-4">
                            {[
                                { Icon: CheckCircle2, text: "60 or 90 min sessions" },
                                { Icon: CheckCircle2, text: "Age: 5 +" },
                                { Icon: CheckCircle2, text: "All levels welcome" },
                                { Icon: MapPin, text: "Frankfurt am Main" },
                            ].map(({ Icon, text }, i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <Icon size={18} className="text-volt mt-1 shrink-0" />
                                    <span className="text-sm text-white/80">{text}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-5 text-xs uppercase tracking-widest text-white/50">
                            <span className="inline-flex items-center gap-2"><Phone size={12} className="text-volt" /> +49 176 56759019</span>
                            <span className="inline-flex items-center gap-2"><Mail size={12} className="text-volt" /> eliteplayeracademy1@gmail.com</span>
                        </div>
                    </div>
                </div>

                <div className="p-8 md:p-16 bg-zinc-950 flex items-start">
                    <div className="w-full max-w-lg">
                        <h2 className="text-3xl md:text-4xl font-heading uppercase">{t("book_your_session")}</h2>
                        <p className="mt-3 text-sm text-white/60">Fill out the form and we'll reach out within 24 hours.</p>
                        {submitted ? (
                            <div data-testid="signup-success" className="mt-8 border border-volt/40 bg-volt/5 p-8">
                                <CheckCircle2 size={32} className="text-volt" />
                                <div className="mt-4 font-heading text-2xl uppercase">You're in.</div>
                                <p className="mt-2 text-white/70 text-sm">Our team has received your application and will contact you at <span className="text-volt">{form.email}</span> shortly.</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="signup-form" className="mt-8 space-y-5">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-white/60">Full Name</Label>
                                    <Input data-testid="signup-fullname" required value={form.full_name} onChange={onChange("full_name")} className="mt-2 bg-zinc-900 border-white/10 focus:border-volt focus:ring-volt rounded-none" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-white/60">Email</Label>
                                        <Input data-testid="signup-email" type="email" required value={form.email} onChange={onChange("email")} className="mt-2 bg-zinc-900 border-white/10 focus:border-volt focus:ring-volt rounded-none" />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-white/60">Phone</Label>
                                        <Input data-testid="signup-phone" required value={form.phone} onChange={onChange("phone")} className="mt-2 bg-zinc-900 border-white/10 focus:border-volt focus:ring-volt rounded-none" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-white/60">Age</Label>
                                        <Input data-testid="signup-age" type="number" min={5} max={60} required value={form.age} onChange={onChange("age")} className="mt-2 bg-zinc-900 border-white/10 focus:border-volt focus:ring-volt rounded-none" />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-white/60">Preferred Date</Label>
                                        <Input data-testid="signup-date" type="date" value={form.preferred_date} onChange={onChange("preferred_date")} className="mt-2 bg-zinc-900 border-white/10 focus:border-volt focus:ring-volt rounded-none" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-white/60">Training Type</Label>
                                        <Select value={form.training_type} onValueChange={(v) => setForm((f) => ({ ...f, training_type: v }))}>
                                            <SelectTrigger data-testid="signup-type" className="mt-2 bg-zinc-900 border-white/10 focus:border-volt rounded-none"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="individual">1-on-1</SelectItem>
                                                <SelectItem value="group">Group</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-white/60">Experience</Label>
                                        <Select value={form.experience_level} onValueChange={(v) => setForm((f) => ({ ...f, experience_level: v }))}>
                                            <SelectTrigger data-testid="signup-level" className="mt-2 bg-zinc-900 border-white/10 focus:border-volt rounded-none"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="beginner">Beginner</SelectItem>
                                                <SelectItem value="intermediate">Intermediate</SelectItem>
                                                <SelectItem value="advanced">Advanced</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-white/60">Your Goals</Label>
                                    <Textarea data-testid="signup-goals" rows={4} value={form.goals} onChange={onChange("goals")}
                                              placeholder="Tell us what you want to work on…"
                                              className="mt-2 bg-zinc-900 border-white/10 focus:border-volt focus:ring-volt rounded-none" />
                                </div>
                                <button data-testid="signup-submit" disabled={loading} className="btn-volt w-full py-4 disabled:opacity-60">
                                    {loading ? "Sending…" : "Submit Application"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
